<template>
  <div class="deferred-oss-uploader">
    <div v-if="fileList.length">
      <div v-for="(file, index) in fileList" :key="file.uid || file.url || index" class="deferred-file-item">
        <span class="deferred-file-name">{{ file.name || file.url }}</span>
        <div class="deferred-file-actions">
          <el-button v-if="canPreviewFile(file)" type="text" size="mini" @click="$emit('preview', file)">查看</el-button>
          <el-button v-if="file.url" type="text" size="mini" @click="$emit('download', file)">下载</el-button>
          <el-button type="text" size="mini" @click="removeFile(index)">删除</el-button>
        </div>
      </div>
    </div>
    <div v-else class="empty-file">暂无文件</div>
    <input ref="fileInput" class="deferred-file-input" type="file" multiple @change="handleFileChange" />
    <el-button type="text" size="mini" class="deferred-add-button" @click="selectFile">+添加</el-button>
    <div v-if="tip" class="deferred-upload-tip">{{ tip }}</div>
  </div>
</template>

<script>
export default {
  name: 'DeferredOssUploader',
  props: {
    value: {
      type: Array,
      default() {
        return []
      }
    },
    dir: {
      type: String,
      default: 'bill'
    },
    limit: {
      type: Number,
      default: 20
    },
    tip: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      fileList: []
    }
  },
  watch: {
    value: {
      handler(val) {
        this.fileList = this.normalizeFiles(val || [])
      },
      immediate: true,
      deep: true
    }
  },
  beforeDestroy() {
    this.revokeLocalUrls(this.fileList)
  },
  methods: {
    selectFile() {
      this.$refs.fileInput.click()
    },
    handleFileChange(event) {
      let files = Array.prototype.slice.call(event.target.files || [])
      event.target.value = ''
      if (!files.length) {
        return
      }
      let remainCount = this.limit - this.fileList.length
      if (remainCount <= 0) {
        this.$message.error('最多上传' + this.limit + '份文件')
        return
      }
      if (files.length > remainCount) {
        this.$message.error('最多上传' + this.limit + '份文件')
        files = files.slice(0, remainCount)
      }
      let newFiles = files.map(file => {
        let localUrl = URL.createObjectURL(file)
        return {
          uid: Date.now() + '_' + Math.random(),
          name: file.name,
          size: file.size,
          raw: file,
          local_url: localUrl,
          url: '',
          file_type: this.getFileType(file.name),
          status: 'pending'
        }
      })
      this.fileList = this.fileList.concat(newFiles)
      this.emitChange()
    },
    removeFile(index) {
      let file = this.fileList[index]
      if (file && file.local_url) {
        URL.revokeObjectURL(file.local_url)
      }
      this.fileList.splice(index, 1)
      this.emitChange()
    },
    canPreviewFile(file) {
      return file && (file.file_type === 'image' || file.file_type === 'video')
    },
    async upload() {
      let result = []
      for (let i = 0; i < this.fileList.length; i++) {
        let file = this.fileList[i]
        if (file.raw && file.status === 'pending') {
          let uploaded = await this.uploadToOss(file)
          result.push(uploaded)
          this.$set(this.fileList, i, uploaded)
        } else {
          result.push({
            url: file.url,
            name: file.name || this.getFileName(file.url),
            size: file.size,
            file_type: file.file_type || this.getFileType(file.name || file.url),
            status: 'success'
          })
        }
      }
      this.fileList = result.filter(item => item.url)
      this.emitChange()
      return this.fileList
    },
    async uploadToOss(fileItem) {
      let raw = fileItem.raw
      let sign = await this.getOssSign()
      let key = this.buildFileKey(sign, raw.name)
      let formData = new FormData()
      formData.append('key', key)
      formData.append('policy', sign.policy)
      formData.append('OSSAccessKeyId', sign.accessid)
      formData.append('success_action_status', '200')
      formData.append('signature', sign.signature)
      formData.append('file', raw)
      await this.postOssFile(sign.host, formData)
      if (fileItem.local_url) {
        URL.revokeObjectURL(fileItem.local_url)
      }
      let url = sign.host.replace(/\/$/, '') + '/' + key
      return {
        uid: fileItem.uid,
        name: raw.name,
        size: raw.size,
        url,
        path: url,
        file_type: fileItem.file_type || this.getFileType(raw.name),
        status: 'success'
      }
    },
    async getOssSign() {
      let ossDir = `${this.$config.ossAppDir}/${this.dir}`
      let sign = await this.$api.getOssSign({ dir: ossDir })
      if (!sign) {
        throw new Error('获取OSS签名失败')
      }
      return sign
    },
    buildFileKey(sign, filename) {
      let name = String(filename || '')
      let extension = name.indexOf('.') > -1 ? name.split('.').pop().toLowerCase() : ''
      let time = new Date().getTime() + Math.random() * 1000
      return sign.dir + time + (extension ? '.' + extension : '')
    },
    postOssFile(url, formData) {
      return new Promise((resolve, reject) => {
        let xhr = new XMLHttpRequest()
        xhr.open('POST', url, true)
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve()
          } else {
            reject(new Error('文件上传失败'))
          }
        }
        xhr.onerror = () => reject(new Error('文件上传失败'))
        xhr.send(formData)
      })
    },
    normalizeFiles(files) {
      return files.map(item => {
        if (item.raw) {
          return item
        }
        let url = typeof item === 'string' ? item : (item.url || item.full_path || item.path || '')
        let name = typeof item === 'string' ? this.getFileName(url) : (item.name || item.title || this.getFileName(url))
        return Object.assign({}, typeof item === 'string' ? {} : item, {
          url,
          name,
          file_type: (typeof item === 'string' ? '' : item.file_type) || this.getFileType(name || url),
          status: 'success'
        })
      }).filter(item => item.url || item.raw)
    },
    emitChange() {
      this.$emit('input', this.fileList)
      this.$emit('change', this.fileList)
    },
    revokeLocalUrls(files) {
      files.forEach(file => {
        if (file.local_url) {
          URL.revokeObjectURL(file.local_url)
        }
      })
    },
    getFileName(url) {
      if (!url) {
        return ''
      }
      return String(url).split('?')[0].split('/').pop()
    },
    getFileType(name) {
      let extension = String(name || '').split('?')[0].split('.').pop().toLowerCase()
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].indexOf(extension) > -1) {
        return 'image'
      }
      if (['mp4', 'mov', 'avi', 'wmv', 'flv', 'mkv', 'webm'].indexOf(extension) > -1) {
        return 'video'
      }
      if (['zip', 'rar', '7z', 'tar', 'gz'].indexOf(extension) > -1) {
        return 'archive'
      }
      return 'file'
    }
  }
}
</script>

<style scoped lang="scss">
.deferred-file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  line-height: 28px;
}

.deferred-file-name {
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.deferred-file-actions {
  flex-shrink: 0;
  margin-left: 12px;
}

.deferred-file-actions .el-button + .el-button {
  margin-left: 6px;
}

.deferred-file-input {
  display: none;
}

.deferred-add-button {
  margin-top: 4px;
}

.deferred-upload-tip {
  margin-top: 8px;
  color: #909399;
  font-size: 12px;
  line-height: 18px;
}

.empty-file {
  color: #909399;
}
</style>
